import { CSSProperties, useState, useEffect, Fragment, useContext } from 'react';
import useCampaignBuilder, { CreativeSelectorPost, transformPostsToCreativeFormat } from 'Hooks/useCampaignBuilder';
import { clsx } from 'modules/Utils';
import Sty from 'modules/Sty';
import Colors from 'modules/Colors';
import ButtonSymphony from 'elements/ButtonSymphony';
import Icon from 'elements/Icon';
import ItemSelector, { ItemSelectorAction } from 'elements/ItemSelector';
import CloudinaryUploadWidget, { CloudinaryUploadedFileType } from 'components/CampaignViews/CloudinaryUploadWidget';
import { AssetPlatformType } from 'components/CampaignViews/AdCreativeSelectorCampaignView';
import { InstagramPost, MediaProductType } from 'modules/Types';
import InputSymphony from 'elements/InputSymphony';
import { getVideoFromUrl_api, ImportFromURLResponse } from 'api/agentApi';
import { toastError, toastSuccess } from 'modules/Toast';
import useMaestroChat from 'Hooks/useMaestroChat';
import useBrand from 'Hooks/useBrand';
import { useFeature } from 'Hooks/useFeature';
import { FeatureKeys } from 'features/definitions';
import useFacebookConnections from 'Hooks/useFacebookConnections';
import { loadInstagramPosts } from 'api/facebookApi';
import { toast } from 'react-toastify';
import Axios from 'helpers/Interceptor';
import { connectToBrand_api, getInstagramBusinessOrCreatorAccount_api, getInstagramBusinessDiscoveryPosts_api, connectToBrand_Unauthenticated_api } from 'api/brandApi';
import { track, setUserProperty } from 'analytics';
import NotFoundInstaUser from 'components/connect/NotFoundInstaUser';
import { ConnectedInstagramAccountType } from 'modules/Types';
import AssistantWorkspaceActions, { ActionButton } from './AssistantWorkspaceActions';
import { WorkspaceTheme } from './AssistantWorkspaceWrapper';
import useScreen from 'Hooks/useScreen';
import { UpgradeModalContext } from 'Hooks/UpgradeModalContext';
import { PlanType } from 'modules/Const';

type MediaTypeFilter = 'all' | 'videos' | 'images' | 'carousels';
type SortOption = 'recent' | 'engaging' | 'views' | 'likes';

interface AssistantWorkspaceInstagramPostSelectionProps {
    workspaceContent: {
        type: 'instagramPostSelection';
        data?: {
            campaignId?: string;
        };
    };
    handleSendMessage: (message: string, hiddenData?: any) => void;
}

const AssistantWorkspaceInstagramPostSelection = ({
    workspaceContent,
    handleSendMessage,
}: AssistantWorkspaceInstagramPostSelectionProps) => {
    const {
        setSelectedInstagramPosts,
        selectedInstagramPosts,
        uploadedAssets,
        setUploadedAssets,
        getUploadedAssets,
        removeUploadedAsset,
        importedFromUrlPosts,
        setImportedFromUrlPosts,
        removeImportedFromUrlPost,
        setPreviewedAsset,
        previewedAsset,
        getAllSelectedPosts,
        removeSelectedInstagramPost,
    } = useCampaignBuilder();

    const { mobileView, mStyle } = useScreen();
    const { handleOpenModal } = useContext(UpgradeModalContext);

    const { isProBrand, currentBrand, setCurrentBrand } = useBrand();
    const { setActiveWorkspace, demoMode, activeWorkspace } = useMaestroChat(); // Added activeWorkspace
    const { selectedInstagramPage } = useFacebookConnections();

    // State for fetching instagram posts
    const [isLoadingInstagramPosts, setIsLoadingInstagramPosts] = useState<boolean>(!activeWorkspace?.data?.isDemo); // Don't autoload if demo
    const [loadedInstagramPosts, setLoadedInstagramPosts] = useState<InstagramPost[]>([]);
    const [hasMorePosts, setHasMorePosts] = useState<boolean>(true);
    const [isLoadingMorePosts, setIsLoadingMorePosts] = useState<boolean>(false);
    const [nextCursor, setNextCursor] = useState<string | null>(null);

    // New state for search and filtering
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [mediaTypeFilter, setMediaTypeFilter] = useState<MediaTypeFilter>('videos'); // Default to videos as recommended
    const [sortOption, setSortOption] = useState<SortOption>('recent');

    // Quick action states
    const [showUploadWidget, setShowUploadWidget] = useState<boolean>(false);
    const [showUrlImport, setShowUrlImport] = useState<boolean>(false);
    const [importFromUrlInput, setImportFromUrlInput] = useState<string>('');
    const [isImportingFromUrl, setIsImportingFromUrl] = useState<boolean>(false);
    const [isConfirming, setIsConfirming] = useState<boolean>(false);

    // Instagram connection state
    const [instagramSearchText, setInstagramSearchText] = useState<string>('');
    const [isConnectingInstagram, setIsConnectingInstagram] = useState<boolean>(false);
    const [isSearchingInstagram, setIsSearchingInstagram] = useState<boolean>(false);
    const [instagramSearchResults, setInstagramSearchResults] = useState<ConnectedInstagramAccountType[]>([]);
    const [instagramInputError, setInstagramInputError] = useState<string | null>(null);
    const [showInstagramErrorComponent, setShowInstagramErrorComponent] = useState<boolean>(false);
    
    // Expander UI state
    const [showSelectedPosts, setShowSelectedPosts] = useState<boolean>(false);
    // Use feature gate to determine post selection limits
    const { getLimit } = useFeature(FeatureKeys.CAMPAIGN_AB_TESTING);
    const limit = getLimit();
    const MAX_POSTS = typeof limit === 'number' ? limit : 1;

    const allSelectedPosts = getAllSelectedPosts('facebook');

    // Check if we should use business discovery API instead of connected pages
    const shouldUseBusinessDiscovery = !selectedInstagramPage && currentBrand?.connections?.business_discovery_instagram?.username;
    const businessDiscoveryUsername = currentBrand?.connections?.business_discovery_instagram?.username;

    // useEffect to handle demo modal trigger for Cloudinary
    useEffect(() => {
        if (activeWorkspace?.data?.isDemo && activeWorkspace?.data?.scenarioId === 'ui_test_instagram_post_selection_cloudinary_modal') {
            console.log("DEMO: Triggering Cloudinary Upload Modal for Instagram Post Selection");
            // This component doesn't directly open Cloudinary modal.
            // It uses <CloudinaryUploadWidget onUpload={handleAssetUpload}>
            // To test this, we'd need to simulate a click on the upload button,
            // or the CloudinaryUploadWidget itself would need a prop to auto-open for demo.
            // For now, we'll log and consider how to best trigger this.
            // A simple way is to expose a state for `showUploadWidget` and set it here.
            setShowUploadWidget(true); // Assuming this state controls its direct visibility or a wrapper.
                                     // If CloudinaryUploadWidget is always modal, this won't work directly.
                                     // We might need to add a button in DemoControls to trigger the upload button click.
        }
    }, [activeWorkspace?.data?.isDemo, activeWorkspace?.data?.scenarioId]);


    /** 
     * Check if user has any Instagram connection (either connected page or business discovery)
     */
    const hasAnyInstagramConnection = () => {
        return !!(selectedInstagramPage || currentBrand?.connections?.business_discovery_instagram?.username);
    };

    /** 
     * Fetch Instagram posts using secure backend API for business discovery
     */
    const fetchBusinessDiscoveryPosts = async (fetchLimit: number = 25, afterCursor?: string) => {
        if (!businessDiscoveryUsername || !currentBrand?.id) {
            throw new Error('Missing business discovery configuration. Please ensure Instagram business account is properly connected.');
        }

        try {
            console.log('Fetching business discovery posts:', {
                businessDiscoveryUsername,
                afterCursor,
                fetchLimit
            });

            const { data: response } = await getInstagramBusinessDiscoveryPosts_api({
                username: businessDiscoveryUsername,
                limit: fetchLimit,
                after: afterCursor || undefined,
                fields: 'like_count,comments_count,caption,thumbnail_url,timestamp,permalink,media_type,media_url,children{media_type,media_url}'
            });

            if (!response?.posts) {
                return { posts: [], paging: { cursors: { after: null } } };
            }

            const rawPosts = response.posts;
            console.log(`Found ${rawPosts.length} posts from business discovery API`);

            // Transform business discovery data to match our expected format
            const posts = rawPosts.map((post: any): InstagramPost => ({
                id: post.id || `bd-${Date.now()}-${Math.random()}`, // Prefix to distinguish from connected posts
                ig_id: post.id,
                permalink: post.permalink,
                caption: post.caption || '',
                media_type: post.media_type,
                media_product_type: MediaProductType.FEED,
                media_url: post.media_url,
                like_count: post.like_count || 0,
                comments_count: post.comments_count || 0,
                timestamp: post.timestamp,
                children: post.children || [],
                username: businessDiscoveryUsername,
                thumbnail_url: post.thumbnail_url,
                selectable: true,
                platformType: AssetPlatformType.INSTAGRAM,
            }));

            console.log(`Transformed ${posts.length} posts for display`);

            const nextCursor = response.paging?.cursors?.after || null;
            console.log('Next cursor for pagination:', nextCursor);

            return {
                posts,
                paging: {
                    cursors: {
                        after: nextCursor
                    }
                }
            };
        } catch (error) {
            console.error('Error fetching business discovery posts:', error);
            console.error('Error details:', {
                error: error instanceof Error ? error.message : error,
                stack: error instanceof Error ? error.stack : undefined,
                businessDiscoveryUsername
            });

            // Provide more specific error messages
            if (error instanceof Error && error.message?.includes('business_discovery')) {
                throw new Error(`Unable to access Instagram posts for @${businessDiscoveryUsername}. The account may be private or not a business account.`);
            }
            throw error;
        }
    };

    /** 
     * Shared function to fetch and process Instagram posts, adapted from AdCreativeSelector
     * Now supports both connected pages and business discovery API
     */
    const fetchAndProcessInstagramPosts = async (isInitialLoad: boolean = false) => {
        console.log('fetchAndProcessInstagramPosts called:', {
            isInitialLoad,
            shouldUseBusinessDiscovery,
            hasSelectedInstagramPage: !!selectedInstagramPage,
            businessDiscoveryUsername,
            currentLoadedPostsCount: loadedInstagramPosts.length
        });

        if (isInitialLoad) {
            setIsLoadingInstagramPosts(true);
            setHasMorePosts(true);
            setNextCursor(null);
        } else {
            if (isLoadingMorePosts || !hasMorePosts || (!nextCursor && !shouldUseBusinessDiscovery)) {
                console.log('Skipping load more due to:', {
                    isLoadingMorePosts,
                    hasMorePosts,
                    nextCursor,
                    shouldUseBusinessDiscovery
                });
                return;
            }
            setIsLoadingMorePosts(true);
        }

        try {
            let response;

            if (shouldUseBusinessDiscovery) {
                console.log('Using business discovery API for @' + businessDiscoveryUsername);
                // Use business discovery API for non-connected accounts
                response = await fetchBusinessDiscoveryPosts(25, isInitialLoad ? undefined : (nextCursor || undefined));
            } else if (selectedInstagramPage) {
                console.log('Using connected Instagram page API');
                // Use existing connected page API
                response = await loadInstagramPosts({
                    instagramPage: selectedInstagramPage,
                    limit: 25,
                    after: isInitialLoad ? undefined : (nextCursor || undefined)
                });
            } else {
                console.warn('No valid Instagram source available');
                // No valid source for Instagram posts
                setHasMorePosts(false);
                return;
            }

            console.log('API response received:', {
                postsCount: response.posts.length,
                hasNextCursor: !!response.paging.cursors.after,
                nextCursor: response.paging.cursors.after
            });

            if (!isInitialLoad && response.posts.length === 0) {
                console.log('No more posts available for pagination');
                setHasMorePosts(false);
                return;
            }

            const sortedPosts = [...response.posts].sort((a, b) => {
                const dateA = new Date(a.timestamp || 0).getTime();
                const dateB = new Date(b.timestamp || 0).getTime();
                return dateB - dateA;
            });

            console.log(`Sorted ${sortedPosts.length} posts by timestamp`);

            if (isInitialLoad) {
                console.log('Setting initial posts');
                setLoadedInstagramPosts(sortedPosts);
            } else {
                console.log('Appending to existing posts');
                setLoadedInstagramPosts(prevPosts => [...prevPosts, ...sortedPosts]);
            }

            setNextCursor(response.paging.cursors.after);
            setHasMorePosts(Boolean(response.paging.cursors.after));

            console.log('State updated:', {
                totalPostsLoaded: isInitialLoad ? sortedPosts.length : loadedInstagramPosts.length + sortedPosts.length,
                hasMorePosts: Boolean(response.paging.cursors.after)
            });
        } catch (error) {
            const errorMessage = shouldUseBusinessDiscovery
                ? `Failed to ${isInitialLoad ? 'load' : 'load more'} Instagram posts from business account`
                : `Failed to ${isInitialLoad ? 'load' : 'load more'} Instagram posts`;

            console.error('Error in fetchAndProcessInstagramPosts:', error);
            toast.error(errorMessage);
            setHasMorePosts(false);
        } finally {
            if (isInitialLoad) {
                setIsLoadingInstagramPosts(false);
            } else {
                setIsLoadingMorePosts(false);
            }
        }
    };

    // Initial fetch for Instagram posts
    useEffect(() => {
        console.log('useEffect for Instagram posts triggered:', {
            hasSelectedInstagramPage: !!selectedInstagramPage,
            shouldUseBusinessDiscovery,
            businessDiscoveryUsername,
            willFetch: !!(selectedInstagramPage || shouldUseBusinessDiscovery)
        });

        // Fetch posts if we have either a connected Instagram page or business discovery username
        // AND it's not a demo scenario that provides its own posts (though this component doesn't currently accept demo posts directly)
        if (!activeWorkspace?.data?.isDemo && (selectedInstagramPage || shouldUseBusinessDiscovery)) {
            console.log('Starting initial Instagram posts fetch...');
            fetchAndProcessInstagramPosts(true);
        } else if (activeWorkspace?.data?.isDemo) {
            console.log('DEMO: Skipping initial Instagram post fetch for demo mode.');
            // Potentially load mock posts here if they were passed in workspaceContent.data
            // For now, the grid will be empty or show "no connection" if not connected.
            setIsLoadingInstagramPosts(false); // Ensure loading state is off for demo
        } else {
            console.log('No Instagram source available, skipping fetch');
            setIsLoadingInstagramPosts(false);
        }
    }, [selectedInstagramPage, shouldUseBusinessDiscovery, businessDiscoveryUsername, activeWorkspace?.data?.isDemo]);

    /** 
     * Filter posts based on search query and media type
     */
    const getFilteredPosts = () => {
        let filtered = loadedInstagramPosts;
        console.log('getFilteredPosts', filtered);
        // Apply search filter
        if (searchQuery) {
            filtered = filtered.filter(post =>
                post.caption?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        // Apply media type filter
        if (mediaTypeFilter !== 'all') {
            filtered = filtered.filter(post => {
                switch (mediaTypeFilter) {
                    case 'videos':
                        return post.media_type === 'VIDEO';
                    case 'images':
                        return post.media_type === 'IMAGE';
                    case 'carousels':
                        return post.media_type === 'CAROUSEL_ALBUM';
                    default:
                        return true;
                }
            });
        }

        // Apply sorting
        const sortedPosts = [...filtered].sort((a, b) => {
            switch (sortOption) {
                case 'recent':
                    return new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime();
                case 'engaging':
                    const aEngagement = (a.like_count || 0) + (a.comments_count || 0);
                    const bEngagement = (b.like_count || 0) + (b.comments_count || 0);
                    return bEngagement - aEngagement;
                case 'views':
                case 'likes':
                    return (b.like_count || 0) - (a.like_count || 0);
                default:
                    return 0;
            }
        });

        console.log('sortedPosts', sortedPosts);

        // Add platformType to posts before returning so transformPostsToCreativeFormat works
        return sortedPosts
    };

    /** Get the appropriate metric to display for a post */
    const getPostMetric = (post: InstagramPost) => {
        // Since video_views is not available, we'll use likes for all post types
        return {
            icon: 'heart',
            value: post.like_count || 0,
            label: post.like_count === 1 ? 'Like' : 'Likes'
        };
    };

    /** Get media type icon for post */
    const getMediaTypeIcon = (post: InstagramPost) => {
        switch (post.media_type) {
            case 'VIDEO':
                return 'play';
            case 'CAROUSEL_ALBUM':
                return 'layers';
            default:
                return null;
        }
    };

    /** 
     * Validate and fetch Instagram business account (adapted from SocialConnectModal)
     */
    const fetchInstagramBusinessAccount = async (username: string): Promise<ConnectedInstagramAccountType[]> => {
        const cleanedUsername = username.trim().toLowerCase().replace(/^@/, '');
        
        // Use hardcoded brand ID 2012 for demo mode, otherwise use current brand ID
        const brandIdToUse = demoMode ? 2012 : currentBrand?.id;
        
        const { data: igProfile } = await getInstagramBusinessOrCreatorAccount_api({
            username: cleanedUsername
        });

        if (!igProfile) {
            setShowInstagramErrorComponent(true);
            throw new Error('No results found. Please check the spelling of your Instagram username and try again.');
        }

        return [igProfile];
    };

    /** 
     * Connect validated Instagram account to brand
     */
    const connectInstagramToBrand = async (instagramData: ConnectedInstagramAccountType) => {
        if (!currentBrand) {
            return;
        }

        try {
            setIsConnectingInstagram(true);

            const updatedCurrentBrand = await connectToBrand_Unauthenticated_api({
                brandId: Number(currentBrand.id),
                brandSlug: String(currentBrand.slug),
                service: 'business_discovery_instagram',
                value: instagramData,
            });

            setCurrentBrand(updatedCurrentBrand);

            // Analytics tracking
            track('Linked Platform', {
                brand_id: currentBrand.id,
                brand_slug: currentBrand.slug,
                brand_name: currentBrand.name,
                platform: 'business_discovery_instagram',
                page: 'Assistant Instagram Workspace',
                from: 'instagram post selection workspace'
            });
            setUserProperty('business_discovery_instagram_linked', true);

            toastSuccess('Instagram connected!');

            // Clear connection state after success
            setInstagramSearchText('');
            setInstagramSearchResults([]);
            setInstagramInputError(null);
            setShowInstagramErrorComponent(false);

        } catch (error) {
            console.error('Error connecting Instagram account:', error);
            toastError('There was an error connecting your Instagram account. Please try again.');
        } finally {
            setIsConnectingInstagram(false);
        }
    };

    /** 
     * Handle Instagram username search and validation
     */
    const handleInstagramUsernameSearch = async () => {
        if (instagramSearchText.length < 2) {
            return;
        }

        setInstagramInputError(null);
        setShowInstagramErrorComponent(false);
        setIsSearchingInstagram(true);

        try {
            const results = await fetchInstagramBusinessAccount(instagramSearchText);
            setInstagramSearchResults(results);
        } catch (error: any) {
            setInstagramSearchResults([]);
            setInstagramInputError(error.message || 'Error validating Instagram account');
        } finally {
            setIsSearchingInstagram(false);
        }
    };

    /** 
     * Handle Instagram connection from search results
     */
    const handleInstagramConnect = async (instagramAccount: ConnectedInstagramAccountType) => {
        await connectInstagramToBrand(instagramAccount);
    };

    const handleConfirmSelection = async () => {
        const campaignId = workspaceContent.data?.campaignId;

        if (!campaignId) {
            toast.error('Campaign ID is required');
            return;
        }

        if (allSelectedPosts.length === 0) {
            toast.error('Please select at least one post');
            return;
        }

        setIsConfirming(true);

        try {
            const posts = allSelectedPosts.map(post => ({
                postUrl: post.permalink,
                postId: post.id,
                caption: post.platformCaption || post.title || '',
                ctaLabel: 'Listen Now',
                mediaUrl: post.assetUrl,
                thumbnailUrl: post.thumbnailUrl,
            }));

            await Axios.post(`/agents/campaign/${campaignId}/instagram-posts`, {
                campaignId,
                posts
            });

            let message = `I just selected ${allSelectedPosts.length} Instagram post${allSelectedPosts.length > 1 ? 's' : ''}!`;

            handleSendMessage(message);
            setActiveWorkspace(null);
        } catch (error) {
            console.error('Error adding Instagram posts to campaign:', error);
            toast.error('Failed to add Instagram posts to campaign. Please try again.');
        } finally {
            setIsConfirming(false);
        }


    };

    /** Handles removing a post based on its type */
    const handleRemovePost = (post: CreativeSelectorPost) => {
        switch (post.originalPostType) {
            case AssetPlatformType.INSTAGRAM:
                removeSelectedInstagramPost(post.id);
                break;
            case AssetPlatformType.CLOUDINARY:
                removeUploadedAsset(post.id);
                break;
            case AssetPlatformType.IMPORTED_FROM_URL:
                removeImportedFromUrlPost(post.id);
                break;
        }
    };

    /** Handles previewing a post */
    const handlePreviewPost = (post: CreativeSelectorPost) => {
        setPreviewedAsset(post);
    };

    /** Handles selecting/deselecting a post */
    const handleSelectPost = (post: CreativeSelectorPost) => {
        const isSelected = allSelectedPosts.some(p => p.id === post.id);

        // If trying to select a second post when MAX_POSTS is 1, show upgrade modal
        if (!isSelected && MAX_POSTS === 1 && allSelectedPosts.length >= 1) {
            handleOpenModal({
                source: 'instagram-post-selection-ab-testing',
                onlyShowPlans: [PlanType.GROWTH],
                highlightedPlan: PlanType.GROWTH,
                upgradeCallToAction: 'Upgrade to Growth to A/B test posts'
            });
            return;
        }

        if (!isSelected && allSelectedPosts.length >= MAX_POSTS) {
            return;
        }

        if (!isSelected) {
            setPreviewedAsset(post);
        }

        switch (post.originalPostType) {
            case AssetPlatformType.INSTAGRAM:
                if (isSelected) {
                    removeSelectedInstagramPost(post.id);
                } else {
                    const originalPost = loadedInstagramPosts.find(p => p.id === post.id);
                    if (originalPost) {
                        setSelectedInstagramPosts([...selectedInstagramPosts, originalPost]);
                    }
                }
                break;
            case AssetPlatformType.CLOUDINARY:
                if (isSelected) {
                    removeUploadedAsset(post.id);
                } else {
                    const originalAsset = getUploadedAssets().find(p => p.public_id === post.id);
                    if (originalAsset) {
                        const currentAssets = getUploadedAssets();
                        setUploadedAssets([...currentAssets, originalAsset]);
                    }
                }
                break;
            case AssetPlatformType.IMPORTED_FROM_URL:
                if (isSelected) {
                    removeImportedFromUrlPost(post.id);
                }
                break;
        }
    };

    /** Handle URL import */
    const handleImportFromUrl = async () => {
        if (!importFromUrlInput.trim()) return;

        setIsImportingFromUrl(true);
        try {
            const res = await getVideoFromUrl_api(importFromUrlInput);
            if (res) {
                setImportedFromUrlPosts([...importedFromUrlPosts, res]);
                const transformedPost = transformPostsToCreativeFormat([res])[0];
                setPreviewedAsset(transformedPost);
                handleSelectPost(transformedPost);
                setShowUrlImport(false);
                setImportFromUrlInput('');
            }
        } catch (error) {
            console.error(error);
            toastError("There was an error importing from your URL. Please try again.");
        } finally {
            setIsImportingFromUrl(false);
        }
    };

    /** Handle successful upload from Cloudinary */
    const handleAssetUpload = (info: CloudinaryUploadedFileType) => {
        const uploadedAssetsState = getUploadedAssets();
        setUploadedAssets([...uploadedAssetsState, info]);
        const transformedPost = transformPostsToCreativeFormat([info])[0];
        setPreviewedAsset(transformedPost);
        handleSelectPost(transformedPost);
        setShowUploadWidget(false);
    };

    /** 
     * Render Instagram input field with icon
     */
    const renderInstagramInput = () => {
        return (
            <div className="instagramInputRow" style={sty.instagramInputRow}>
                {!mobileView && (<Icon 
                    icon="instagram" 
                    size={50} 
                    className="instagramConnectIcon" 
                    style={sty.instagramConnectIcon}
                />)}
                <InputSymphony
                    className="instagramSearchInput"
                    style={sty.instagramSearchInput}
                    placeholder="Enter your Instagram username"
                    value={instagramSearchText}
                    isLoading={isSearchingInstagram}
                    disabled={isSearchingInstagram || isConnectingInstagram}
                    onChange={(valueText) => {
                        setInstagramSearchText(valueText);
                        setInstagramInputError(null);
                        setShowInstagramErrorComponent(false);
                    }}
                    isSearch
                    onSearch={handleInstagramUsernameSearch}
                    onEnter={handleInstagramUsernameSearch}
                />
            </div>
        );
    };

    /** 
     * Render input error message
     */
    const renderInstagramInputError = () => {
        if (!instagramInputError) return null;

        return (
            <div className="instagramInputError" style={sty.instagramInputError}>
                {instagramInputError}
            </div>
        );
    };

    /** 
     * Render error component (NotFoundInstaUser)
     */
    const renderInstagramErrorComponent = () => {
        if (!showInstagramErrorComponent) return null;

        return <NotFoundInstaUser />;
    };

    /** 
     * Render Instagram search results with connect buttons
     */
    const renderInstagramSearchResults = () => {
        if (instagramSearchResults.length === 0) return null;

        return (
            <div className="instagramSearchResults" style={sty.instagramSearchResults}>
                {instagramSearchResults.map((account, index) => {
                    const username = account.username?.toLowerCase();
                    return (
                        <div 
                            key={account.id || index}
                            className="instagramAccountRow" 
                            style={sty.instagramAccountRow}
                        >
                            <img
                                className="instagramAccountImage"
                                src={account.profile_picture_url || require('../../assets/images/profileIcon.svg').default}
                                alt={username}
                                style={sty.instagramAccountImage}
                                onError={(e) => {
                                    e.currentTarget.src = require('../../assets/images/profileIcon.svg').default;
                                }}
                            />
                            <div className="instagramAccountInfo" style={sty.instagramAccountInfo}>
                                <div className="instagramAccountUsername" style={sty.instagramAccountUsername}>
                                    @{username}
                                </div>
                                <div className="instagramAccountFollowers" style={sty.instagramAccountFollowers}>
                                    {account.followers_count?.toLocaleString() || 0} followers
                                </div>
                            </div>
                            <ButtonSymphony
                                className="instagramConnectButton"
                                onClick={() => handleInstagramConnect(account)}
                                disabled={isConnectingInstagram}
                                isLoading={isConnectingInstagram}
                                variant="primary"
                            >
                                Connect
                            </ButtonSymphony>
                        </div>
                    );
                })}
            </div>
        );
    };

    /** 
     * Main Instagram connection UI
     */
    const renderConnectInstagramUI = () => {
        return (
            <div className="connectInstagramContainer" style={sty.connectInstagramContainer}>
                <div className="connectInstagramHeader" style={sty.connectInstagramHeader}>
                    <Icon icon="instagram" size={48} className="connectInstagramIcon" />
                    <h2 style={sty.connectInstagramTitle}>Connect your Instagram Account</h2>
                    <p style={sty.connectInstagramDescription}>
                        Connect your Instagram account to select posts from your feed and use them in your campaigns. You'll need to have a creator or business account to connect.
                    </p>
                </div>

                <div className="connectInstagramForm" style={sty.connectInstagramForm}>
                    {renderInstagramInput()}
                    {renderInstagramInputError()}
                    {renderInstagramErrorComponent()}
                    {renderInstagramSearchResults()}
                </div>
            </div>
        );
    };

    const renderHeader = () => {
        const mediaTypeFilters = [
            { id: 'all', label: 'All' },
            { id: 'videos', label: 'Videos' },
            { id: 'images', label: 'Images' },
            { id: 'carousels', label: 'Carousels' },
        ];

        const sortOptions = [
            { id: 'recent', label: 'Most Recent' },
            { id: 'engaging', label: 'Most Engaging' },
            { id: 'likes', label: 'Most Likes' },
        ];

        // Determine the source of posts for user information
        const getPostSource = () => {
            if (selectedInstagramPage) {
                return `@${selectedInstagramPage.instagramAccount?.username || 'Connected Account'}`;
            }
            if (shouldUseBusinessDiscovery && businessDiscoveryUsername) {
                return `@${businessDiscoveryUsername}`;
            }
            return 'Select a Post';
        };

        let instagramUsername;
        if (selectedInstagramPage) {
            instagramUsername = selectedInstagramPage.instagramAccount?.username;
        }
        if (shouldUseBusinessDiscovery && businessDiscoveryUsername) {
            instagramUsername = businessDiscoveryUsername;
        }

        return (
            <div className="workspaceHeader" style={mStyle(sty, "header")}>
                <div className="headerTop" style={sty.headerTop}>
                    <div className="titleSection" style={sty.titleSection}>
                        <Icon icon="instagram" size={14} className="instagramIcon" />
                        <h2 style={sty.title}>{getPostSource()}</h2>
                    </div>
                </div>

                <InputSymphony
                    className="searchInput"
                    value={searchQuery}
                    onChange={(value) => setSearchQuery(value as string)}
                    placeholder="Search captions..."
                    isSearch
                />

                <div className="filtersAndSort" style={mStyle(sty, "filtersAndSort")}>
                    <div className="mediaTypeFilters" style={mStyle(sty, "mediaTypeFilters")}>
                        {mediaTypeFilters.map(filter => (
                            <ButtonSymphony
                                key={filter.id}
                                className={clsx('mediaTypeFilter', { active: mediaTypeFilter === filter.id })}
                                variant={mediaTypeFilter === filter.id ? 'primary' : 'text'}
                                size="small"
                                onClick={() => setMediaTypeFilter(filter.id as MediaTypeFilter)}
                            >
                                {filter.label}
                            </ButtonSymphony>
                        ))}
                    </div>

                    <select
                        value={sortOption}
                        onChange={(e) => setSortOption(e.target.value as SortOption)}
                        style={mStyle(sty, "sortSelect")}
                    >
                        {sortOptions.map(option => (
                            <option key={option.id} value={option.id}>
                                Sort by: {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        );
    };

    const renderPostGrid = () => {
        // If no Instagram source is available, show a message
        if (!selectedInstagramPage && !shouldUseBusinessDiscovery) {
            return (
                <div className="noInstagramSource" style={sty.noInstagramSource}>
                    <Icon icon="instagram" size={48} className="noSourceIcon" />
                    <h3 style={sty.noSourceTitle}>No Instagram Account Connected</h3>
                    <p style={sty.noSourceDescription}>
                        Connect your Instagram account to select posts from your feed, or use the Upload and Import options above.
                    </p>
                </div>
            );
        }

        const filteredPosts = getFilteredPosts();
        const transformedPosts = transformPostsToCreativeFormat(filteredPosts);
        // Enhanced post items with metrics and media type indicators
        const enhancedPosts = transformedPosts.map(post => {
            const originalPost = filteredPosts.find(p => p.id === post.id);
            if (!originalPost) return post;

            const metric = getPostMetric(originalPost);
            const mediaIcon = getMediaTypeIcon(originalPost);

            // For videos, ensure we have a proper thumbnail or fallback
            let enhancedPost = { ...post };
            if (originalPost.media_type === 'VIDEO' && originalPost.media_url) {
                // For Instagram videos, use the video URL as the asset URL
                // ItemSelector should handle video display
                enhancedPost.assetUrl = originalPost.media_url;

                // Try to extract a thumbnail from Instagram video URL or use a placeholder
                if (!enhancedPost.thumbnailUrl || enhancedPost.thumbnailUrl === originalPost.media_url) {
                    // Instagram doesn't provide separate thumbnails, so we'll use the video URL
                    // and let the browser/ItemSelector handle it
                    enhancedPost.thumbnailUrl = originalPost.media_url;
                }
            }

            return {
                ...enhancedPost,
                customOverlay: (
                    <div className="postOverlays" style={sty.postOverlays}>
                        {mediaIcon && (
                            <div className="mediaTypeIcon" style={sty.mediaTypeIcon}>
                                <Icon icon={mediaIcon} size={16} color="white" className="mediaTypeIcon" />
                            </div>
                        )}
                        <div className="postMetric" style={sty.postMetric}>
                            <Icon icon={metric.icon} size={14} color="white" className="metricIcon" />
                            <span>{metric.value.toLocaleString()} {metric.label}</span>
                        </div>
                    </div>
                )
            };
        });

        const selectPostOptions: ItemSelectorAction[] = [
            {
                hideDefaultState: false, // Hide by default, show on hover
                hideSelectedState: false,
                title: 'Select',
                titleSelected: 'Selected',
                buttonSize: mobileView ? 'small' : 'medium',
                buttonVariant: 'primary',
                buttonVariantSelected: 'outlined',
                onClick: handleSelectPost,
            },
        ];

        return (
            <ItemSelector
                className="instagramPostGrid"
                items={enhancedPosts}
                selectedItems={allSelectedPosts}
                onSelectItem={handleSelectPost}
                isLoading={isLoadingInstagramPosts}
                actions={selectPostOptions}
                layout="vertical"
                onLoadMore={hasMorePosts ? () => fetchAndProcessInstagramPosts(false) : undefined}
                isLoadingMore={isLoadingMorePosts}
            />
        );
    };


    // Check if user has any Instagram connection - if not, show connect UI
    if (!hasAnyInstagramConnection()) {
        return (
            <div className="assistantWorkspaceInstagramPostSelection" style={sty.container}>
                <div className="contentArea" style={mStyle(sty, "contentArea")}>
                    {renderConnectInstagramUI()}
                </div>
            </div>
        );
    }

    // User has Instagram connection - show post selection UI
    const confirmSelectionActions: ActionButton[] = [
        {
            text: 'Confirm Selection',
            onClick: handleConfirmSelection,
            variant: 'primary',
            loading: isConfirming,
            disabled: allSelectedPosts.length === 0 || isConfirming,
        }
    ];

    /** Render selected posts expander UI */
    const renderSelectedPostsPreview = () => {
        if (allSelectedPosts.length === 0) return null;

        const previewLimit = 3;
        const hasMore = allSelectedPosts.length > previewLimit;
        const postsToShow = showSelectedPosts ? allSelectedPosts : allSelectedPosts.slice(0, previewLimit);

        return (
            <div className="selectedPostsPreview" style={mStyle(sty, "selectedPostsPreview")}>
                {hasMore && (
                    <div className="selectedPostsHeader" style={sty.selectedPostsHeader}>
                        <ButtonSymphony
                            className="expanderButton"
                            variant="text"
                            size="small"
                            onClick={() => setShowSelectedPosts(!showSelectedPosts)}
                        >
                            {showSelectedPosts ? 'Show less' : `View all ${allSelectedPosts.length}`}
                        </ButtonSymphony>
                    </div>
                )}
                <div className="selectedPostsGrid" style={sty.selectedPostsGrid}>
                    {postsToShow.map((post, index) => (
                        <div key={post.id} className="selectedPostThumbnail" style={sty.selectedPostThumbnail}>
                            <img 
                                src={post.thumbnailUrl || post.assetUrl} 
                                alt={post.title || `Post ${index + 1}`}
                                style={sty.selectedPostImage}
                            />
                            <ButtonSymphony
                                className="removePostButton"
                                variant="text"
                                size="small"
                                style={sty.removePostButton}
                                onClick={() => handleRemovePost(post)}
                            >
                                <Icon icon="close" size={16} className="closeIcon" />
                            </ButtonSymphony>
                        </div>
                    ))}
                    {!showSelectedPosts && hasMore && (
                        <div className="moreIndicator" style={sty.moreIndicator}>
                            <span>+{allSelectedPosts.length - previewLimit}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="assistantWorkspaceInstagramPostSelection" style={sty.container}>
            {renderHeader()}

            <div className="contentArea" style={mStyle(sty, "contentArea")}>
                {renderPostGrid()}
            </div>

            {allSelectedPosts.length > 0 && renderSelectedPostsPreview()}

            <AssistantWorkspaceActions
                description={MAX_POSTS > 10 ? `${allSelectedPosts.length} selected` : `${allSelectedPosts.length} / ${MAX_POSTS} selected`}
                buttons={confirmSelectionActions}
            />
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        backgroundColor: WorkspaceTheme.primary,
    },
    header: {
        padding: '20px 24px',
        
        borderBottom: `1px solid ${WorkspaceTheme.border}`,
        flexShrink: 0,
        backgroundColor: WorkspaceTheme.primary,
    },
    header_mobile: {
        padding: '16px 16px',
    },
    headerTop: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
    },
    titleSection: {
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
    },
    title: {
        fontSize: 20,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
        margin: 0,
    },
    accountInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    accountText: {
        fontSize: 14,
        color: WorkspaceTheme.textSecondary,
        fontWeight: 500,
    },
    accountType: {
        fontSize: 12,
        color: WorkspaceTheme.textTertiary,
        fontStyle: 'italic',
    },
    quickActions: {
        display: 'flex',
        gap: 8,
    },
    filtersAndSort: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 16,
    },
    filtersAndSort_mobile: {
        flexDirection: 'column',
    },
    mediaTypeFilters: {
        display: 'flex',
        gap: 12,
    },
    mediaTypeFilters_mobile: {
        width: '100%',
        gap: 16

    },
    sortSelect: {
        padding: '8px 12px',
        borderRadius: 6,
        border: `1px solid ${WorkspaceTheme.border}`,
        backgroundColor: WorkspaceTheme.surface,
        fontSize: 14,
        color: WorkspaceTheme.textPrimary,
        cursor: 'pointer',
    },
    sortSelect_mobile: {
        width: '100%',
    },
    contentArea: {
        flex: 1,
        overflowY: 'auto',
        padding: '16px 24px 0',
        backgroundColor: WorkspaceTheme.primary,
    },
    contentArea_mobile: {
        padding: '12px 0 0',
        overflowX: 'hidden',
    },
    postOverlays: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
    },
    mediaTypeIcon: {
        position: 'absolute',
        top: 8,
        right: 8,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        borderRadius: 4,
        padding: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    postMetric: {
        position: 'absolute',
        bottom: 8,
        left: 8,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        borderRadius: 4,
        padding: '4px 8px',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 12,
        color: 'white',
        fontWeight: 500,
    },

    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    modalContent: {
        backgroundColor: WorkspaceTheme.surface,
        borderRadius: 12,
        padding: 24,
        minWidth: 400,
        maxWidth: 500,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        border: `1px solid ${WorkspaceTheme.border}`,
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
    },
    uploadButton: {
        borderStyle: 'dashed',
        backgroundColor: WorkspaceTheme.selected,
        borderWidth: 1,
        borderColor: WorkspaceTheme.accent,
        borderRadius: 8,
        padding: 16,
        color: WorkspaceTheme.accent,
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        height: 72,
    },
    noInstagramSource: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        textAlign: 'center',
        color: WorkspaceTheme.textSecondary,
    },
    noSourceTitle: {
        fontSize: 18,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
        margin: '16px 0 8px 0',
    },
    noSourceDescription: {
        fontSize: 14,
        color: WorkspaceTheme.textSecondary,
        maxWidth: 400,
        lineHeight: 1.5,
        margin: 0,
    },
    // Instagram connection UI styles
    connectInstagramContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        textAlign: 'center',
        height: '100%',
        backgroundColor: WorkspaceTheme.primary,
    },
    connectInstagramHeader: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: 32,
    },
    connectInstagramTitle: {
        fontSize: 24,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
        margin: '16px 0 8px 0',
    },
    connectInstagramDescription: {
        fontSize: 16,
        color: WorkspaceTheme.textSecondary,
        maxWidth: 500,
        lineHeight: 1.5,
        margin: 0,
    },
    connectInstagramForm: {
        width: '100%',
        maxWidth: 500,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    instagramInputRow: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 12,
    },
    instagramConnectIcon: {
        flexShrink: 0,
    },
    instagramSearchInput: {
        flex: 1,
    },
    instagramInputError: {
        color: Colors.red,
        fontSize: 14,
        textAlign: 'left',
    },
    instagramSearchResults: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        borderRadius: 8,
        border: `1px solid ${WorkspaceTheme.border}`,
        backgroundColor: WorkspaceTheme.surface,
        overflow: 'hidden',
    },
    instagramAccountRow: {
        display: 'flex',
        alignItems: 'center',
        padding: 16,
        borderBottom: `1px solid ${WorkspaceTheme.borderLight}`,
        gap: 12,
    },
    instagramAccountImage: {
        width: 48,
        height: 48,
        borderRadius: 8,
        objectFit: 'cover',
        flexShrink: 0,
    },
    instagramAccountInfo: {
        flex: 1,
        textAlign: 'left',
    },
    instagramAccountName: {
        fontSize: 16,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
        marginBottom: 4,
    },
    instagramAccountUsername: {
        fontSize: 14,
        color: WorkspaceTheme.textPrimary,
        marginBottom: 4,
    },
    instagramAccountFollowers: {
        fontSize: 12,
        color: WorkspaceTheme.textSecondary,
    },
    // Selected posts preview styles
    selectedPostsPreview: {
        padding: '16px 24px',
        borderTop: `1px solid ${WorkspaceTheme.border}`,
        backgroundColor: WorkspaceTheme.surface,
    },
    selectedPostsPreview_mobile: {
        padding: '8px 16px',
    },
    selectedPostsHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    selectedPostsCount: {
        fontSize: 14,
        fontWeight: 600,
        color: WorkspaceTheme.textPrimary,
    },
    selectedPostsGrid: {
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
        alignItems: 'center',
    },
    selectedPostThumbnail: {
        position: 'relative',
        width: 60,
        height: 60,
        borderRadius: 8,
        overflow: 'hidden',
        border: `2px solid ${WorkspaceTheme.accent}`,
    },
    selectedPostImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    removePostButton: {
        position: 'absolute',
        top: -4,
        right: -4,
        backgroundColor: WorkspaceTheme.surface,
        borderRadius: '50%',
        padding: 2,
        minWidth: 20,
        height: 20,
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    },
    moreIndicator: {
        width: 60,
        height: 60,
        borderRadius: 8,
        backgroundColor: WorkspaceTheme.selected,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        fontWeight: 600,
        color: WorkspaceTheme.textSecondary,
        border: `1px dashed ${WorkspaceTheme.border}`,
    },
};

export default AssistantWorkspaceInstagramPostSelection; 